import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import { useSync } from "../../hooks/sync";
import {
  setCurrentDeckDetails,
  updateLastRefreshed,
} from "../../redux/features/deck/deckSlice";
import {
  findAllCardsByDeckId,
  updateCard,
} from "../../services/data/cardDataService";
import IOSSwitch from "../common/IOSSwitch";
import IOSSlider from "../common/IOSSlider";
import AppDialog from "../common/AppDialog";
import { findDeckById, updateDeck } from "../../services/data/deckDataService";

const UpdateDeck = ({ open, onClose, deckId }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [resetDeck, setResetDeck] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOption, setSelectedOption] = useState("cram");
  const [easeFactor, setEaseFactor] = useState(1.3);
  const [interval, setInterval] = useState("0.01");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const studyModeOptions = {
    cram: { easeFactor: 1.3, interval: 0.01 },
    standard: { easeFactor: 2.5, interval: 1 },
    casual: { easeFactor: 3.5, interval: 7 },
    custom: { easeFactor: easeFactor, interval: interval },
  };
  const sync = useSync();
  const now = new Date().toISOString();
  const dispatch = useDispatch();

  useEffect(() => {
    const getDeckDetails = async () => {
      if (open && deckId) {
        setIsLoading(true);
        try {
          const deck = await findDeckById(deckId);
          if (deck) {
            setIsLoading(true);
            setTabValue(0);
            setResetDeck(false);
            setName(deck.name);
            setDescription(deck.description);
            setSelectedOption(deck.studyMode || "cram");
            setEaseFactor(
              deck.easeFactor !== undefined ? deck.easeFactor : 1.3
            );
            setInterval(deck.interval !== undefined ? deck.interval : "0.01");
            setIsLoading(false);
          }
        } catch (error) {
          console.error("Error fetching deck details:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    getDeckDetails();
  }, [deckId, open]);

  const handleChange = (event) => {
    setInterval(event.target.value);
  };

  const validateDecimalInput = (value, setterFunction) => {
    if (/^-?\d*\.?\d*$/.test(value)) {
      setterFunction(value);
    }
  };

  const handleStudyModeChange = (event) => {
    const newStudyMode = event.target.value;
    setSelectedOption(newStudyMode);

    if (newStudyMode !== "custom") {
      const { easeFactor, interval } = studyModeOptions[newStudyMode];
      setEaseFactor(easeFactor);
      setInterval(interval.toString());
    }
  };

  const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    "& .MuiToggleButtonGroup-grouped": {
      margin: theme.spacing(1),
      border: 0,
      "&:not(:first-of-type)": {
        borderRadius: theme.shape.borderRadius,
      },
      "&:first-of-type": {
        borderRadius: theme.shape.borderRadius,
      },
    },
  }));

  const handleSliderChange = (event, newValue) => {
    setEaseFactor(newValue);
  };

  const handleClose = () => {
    onClose();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    try {
      // Always update deck information
      const deckUpdateData = {
        name,
        description,
      };

      // Only include these in the update if resetDeck is true
      if (resetDeck) {
        deckUpdateData.studyMode = selectedOption;
        deckUpdateData.easeFactor = parseFloat(easeFactor);
        deckUpdateData.interval = parseFloat(interval);
      }

      await updateDeck(deckId, deckUpdateData);

      if (resetDeck) {
        const cards = await findAllCardsByDeckId(deckId);

        const cardUpdates = {
          easeFactor: parseFloat(easeFactor),
          interval: parseFloat(interval),
          repetitions: 0,
          reviewAt: now,
        };

        await Promise.all(
          cards.map((card) => {
            return updateCard(card.id, cardUpdates);
          })
        );
      }

      const updatedDeckDetails = await findDeckById(deckId);
      if (updatedDeckDetails) {
        dispatch(setCurrentDeckDetails(updatedDeckDetails));
      }

      onClose();
      sync();
    } catch (error) {
      console.error("Error updating deck and cards:", error);
    } finally {
      const newRefreshTime = new Date().toISOString();
      dispatch(updateLastRefreshed(newRefreshTime));
    }
  };

  return (
    <AppDialog open={open} onClose={handleClose} maxWidth="md">
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Deck Settings
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,

          borderRadius: ".5rem",
          my: 1,
          mx: "auto",
        }}
      >
        <StyledToggleButtonGroup
          size="small"
          exclusive
          value={tabValue}
          onChange={handleTabChange}
          aria-label="deck details"
          sx={{ display: "flex", justifyContent: "center" }}
        >
          <ToggleButton value={0} aria-label="details">
            <AssignmentRoundedIcon sx={{ mr: 1 }} />
            General
          </ToggleButton>
          <ToggleButton value={1} aria-label="advanced-options">
            <TuneRoundedIcon sx={{ mr: 1 }} />
            Advanced
          </ToggleButton>
        </StyledToggleButtonGroup>
      </Box>

      {isLoading ? (
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      ) : (
        <form onSubmit={handleFormSubmit}>
          <DialogContent
            sx={{
              padding: "10px 24px 10px 24px",
            }}
          >
            {tabValue === 0 && (
              <>
                <TextField
                  autoFocus
                  margin="dense"
                  id="deckName"
                  name="deckName"
                  label="Deck Name"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
                <TextField
                  margin="dense"
                  id="deckDescription"
                  name="deckDescription"
                  label="Description"
                  multiline
                  rows={5}
                  variant="outlined"
                  fullWidth
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </>
            )}
            {tabValue === 1 && (
              <Stack spacing={1}>
                <Box>
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={resetDeck}
                        onChange={(e) => setResetDeck(e.target.checked)}
                      />
                    }
                    label="Reset Deck"
                  />
                </Box>

                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="flex-start"
                  justifyContent="space-evenly"
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      // border: 1,
                      // borderColor: "blue",
                      // borderStyle: "solid",
                      p: 2,
                      width: "50%",
                    }}
                  >
                    {/* <HtmlTooltip
                      title={
                        <React.Fragment>
                          <Typography color="inherit">Tooltip with HTML</Typography>
                          <em>{"And here's"}</em> <b>{"some"}</b> <u>{"amazing content"}</u>. {"It's very engaging. Right?"}
                        </React.Fragment>
                      }></HtmlTooltip> */}

                    <Typography variant="h6" color="text.primary" gutterBottom>
                      Study Mode
                    </Typography>

                    <Stack
                      spacing={1}
                      alignItems="flex-start"
                      justifyContent="space-evenly"
                    >
                      <FormControl size="small" fullWidth>
                        <Select
                          disabled={!resetDeck}
                          value={selectedOption}
                          onChange={handleStudyModeChange}
                          displayEmpty
                          inputProps={{ "aria-label": "Without label" }}
                        >
                          <MenuItem value="cram">Cram</MenuItem>
                          <MenuItem value="standard">Standard</MenuItem>
                          <MenuItem value="casual">Casual</MenuItem>
                          <MenuItem value="custom">Custom</MenuItem>
                          {/* <MenuItem disabled value="uniformInterval">
                            Fixed
                          </MenuItem> */}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Box>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 2, height: "auto" }}
                  />
                  <Box
                    sx={{
                      flexGrow: 1,
                      p: 2,
                      width: "50%",
                    }}
                  >
                    <Typography variant="h6" color="text.primary" gutterBottom>
                      Spaced Repetition Options
                    </Typography>
                    <Stack
                      spacing={1}
                      alignItems="flex-start"
                      justifyContent="space-evenly"
                    >
                      <Box sx={{ width: "100%" }}>
                        <Typography id="ease-factor-slider" gutterBottom>
                          Ease Factor:
                        </Typography>
                        <IOSSlider
                          disabled={!resetDeck || selectedOption !== "custom"}
                          aria-label="ios slider"
                          value={easeFactor}
                          valueLabelDisplay="on"
                          onChange={handleSliderChange}
                          min={1.3}
                          max={3.5}
                          step={0.1}
                          sx={{ my: 2 }}
                        />
                        <Typography
                          id="custom-select-label"
                          gutterBottom
                          sx={{ mb: 2 }}
                        >
                          Starting Review Interval:
                        </Typography>
                        <FormControl size="small" fullWidth>
                          <Select
                            sx={{ mb: 2 }}
                            disabled={!resetDeck || selectedOption !== "custom"}
                            labelId="custom-select-label"
                            id="custom-select"
                            value={interval}
                            onChange={handleChange}
                          >
                            {/* TODO: Remove Test Case */}
                            <MenuItem value={0.000694}>1 minute</MenuItem>
                            <MenuItem value={0.01}>15 minutes</MenuItem>
                            <MenuItem value={0.04}>1 hour</MenuItem>
                            <MenuItem value={0.17}>4 hours</MenuItem>
                            <MenuItem value={1}>1 day</MenuItem>
                            <MenuItem value={2}>2 days</MenuItem>
                            <MenuItem value={3}>3 days</MenuItem>
                            <MenuItem value={7}>1 week</MenuItem>
                            <MenuItem value={14}>2 weeks</MenuItem>
                            <MenuItem value={30}>1 month</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {selectedOption === "uniformInterval" && (
                        <Box sx={{ width: "100%" }}>
                          <Typography
                            id="uniformInterval-select-label"
                            gutterBottom
                            sx={{ mb: 2 }}
                          >
                            Fixed Review Interval:
                          </Typography>
                          <FormControl size="small">
                            <Select
                              labelId="uniformInterval-label"
                              id="uniformInterval-select"
                              value={interval}
                              onChange={handleChange}
                            >
                              <MenuItem value={0.01}>15 minutes</MenuItem>
                              <MenuItem value={0.04}>1 hour</MenuItem>
                              <MenuItem value={0.17}>4 hours</MenuItem>
                              <MenuItem value={1}>1 day</MenuItem>
                              <MenuItem value={2}>2 days</MenuItem>
                              <MenuItem value={3}>3 days</MenuItem>
                              <MenuItem value={7}>1 week</MenuItem>
                              <MenuItem value={14}>2 weeks</MenuItem>
                              <MenuItem value={30}>1 month</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              </Stack>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              padding: "10px 24px 20px 24px",
            }}
          >
            <Button variant="outlined" onClick={onClose} fullWidth>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" fullWidth>
              Save
            </Button>
          </DialogActions>
        </form>
      )}
    </AppDialog>
  );
};

export default UpdateDeck;
